/**
 * Frontend JavaScript for SP Resource List block.
 *
 * Handles progressive enhancement for the filter UI.
 */
document.addEventListener( 'DOMContentLoaded', () => {
	// Find all resource list blocks on the page
	const resourceListBlocks = document.querySelectorAll(
		'.wp-block-sprypoint-resource-list'
	);

	if ( ! resourceListBlocks || resourceListBlocks.length === 0 ) {
		return; // No blocks to enhance
	}

	resourceListBlocks.forEach( ( blockElement, index ) => {
		// Defensive check for block element
		if ( ! blockElement ) {
			console.warn(
				`SP Resource List: Iteration ${ index }: Invalid block element found.`
			); // Keep warning
			return; // Skip this iteration
		}
		const blockId = blockElement.id || `block-index-${ index }`; // Use ID or index for logging

		// --- Store Block State ---
		let currentFilterType = ''; // Initial active filter is empty (show all selected/all)
		const selectionMode = blockElement.dataset.querySelectionMode || 'all';
		let selectedResourceTypes = [];
		try {
			const typesJson = blockElement.dataset.querySelectedTypes;
			if ( typesJson ) {
				selectedResourceTypes = JSON.parse( typesJson );
				if ( ! Array.isArray( selectedResourceTypes ) ) {
					console.warn(
						`SP Resource List (${ blockId }): Invalid data-query-selected-types format (not an array).`
					);
					selectedResourceTypes = [];
				}
			}
		} catch ( e ) {
			console.error(
				`SP Resource List (${ blockId }): Error parsing data-query-selected-types JSON.`, e
			);
			selectedResourceTypes = [];
		}
		const postsPerPageRaw = blockElement.dataset.postsPerPage || '1';
		let postsPerPage = parseInt( postsPerPageRaw, 10 );
		if ( isNaN( postsPerPage ) || postsPerPage <= 0 ) {
			postsPerPage = 1; // Default to 1 on error
		}
		const orderBy = blockElement.dataset.queryOrderby || 'date';
		const order = blockElement.dataset.queryOrder || 'DESC';
		const ids = blockElement.dataset.queryIds || '';
		// ------------------------

		// Find the filter form *before* this block element
		const filterForm = blockElement.previousElementSibling;

		if (
			! filterForm ||
			! filterForm.classList.contains( 'sp-resource-list-filter' )
		) {
			// No filter form found, skip filter setup
		} else {
			// Only setup filter listeners if the form was found
			const radioButtons = filterForm.querySelectorAll(
				'input[type="radio"][name="resource_type_filter"]'
			);

			if ( ! radioButtons || radioButtons.length === 0 ) {
				console.warn(
					`SP Resource List (${ blockId }): No radio buttons found within the filter form.`
				); // Keep warning
			} else {
				// Add listener to each radio button for the 'change' event
				radioButtons.forEach( ( radio ) => {
					radio.addEventListener( 'change', ( event ) => {
						try {
							const newFilterType = event.target.value;
							if ( typeof newFilterType === 'undefined' ) {
								console.warn(
									`SP Resource List (${ blockId }): Change event fired but value is undefined.`
								); // Keep warning
								return;
							}

							// Update the stored active filter for this block
							currentFilterType = newFilterType;
							// Reset pagination when filter changes
							blockElement.dataset.currentPage = 1;

							blockElement.style.opacity = '0.5';

							// Construct API URL with all necessary parameters
							const params = new URLSearchParams();
							params.append( 'selectionMode', selectionMode );
							params.append( 'postsPerPage', postsPerPage );
							params.append( 'resource_type', currentFilterType ); // The new active filter
							if ( selectionMode === 'taxonomy' ) {
								// Only send selectedResourceTypes if in taxonomy mode
								params.append(
									'selectedResourceTypes',
									JSON.stringify( selectedResourceTypes )
								);
							}
							// Note: orderBy and order are not currently passed to the filter endpoint,
							// it uses defaults. Enhance API if sorting is needed on filter.

							const apiUrl = `/wp-json/sprypoint/v1/resource-list-filter?${ params.toString() }`;

							fetch( apiUrl )
								.then( ( response ) => {
									if ( ! response.ok ) {
										return response
											.json()
											.then( ( errData ) => {
												throw new Error(
													`HTTP error ${
														response.status
													}. Message: ${
														errData?.message ||
														'Unknown server error'
													}`
												);
											} )
											.catch( () => {
												throw new Error(
													`HTTP error! Status: ${ response.status }`
												);
											} );
									}
									return response.json();
								} )
								.then( ( data ) => {
									if (
										data &&
										typeof data.html === 'string' &&
										typeof data.totalPages === 'number' &&
										data.totalPages >= 0
									) {
										blockElement.innerHTML = data.html;
										const newTotalPages = data.totalPages;
										blockElement.dataset.totalPages =
											newTotalPages;
										blockElement.dataset.currentPage = 1;

										const loadMoreWrapper =
											blockElement.nextElementSibling;
										let loadMoreButton;
										if (
											loadMoreWrapper &&
											loadMoreWrapper.classList.contains(
												'sp-resource-list-load-more-wrapper'
											)
										) {
											loadMoreButton =
												loadMoreWrapper.querySelector(
													'.sp-resource-list-load-more'
												);
										}
										if ( loadMoreButton ) {
											if ( newTotalPages > 1 ) {
												loadMoreWrapper.style.display =
													'';
												loadMoreButton.disabled = false;
												loadMoreButton.textContent =
													'Load more';
											} else {
												loadMoreWrapper.style.display =
													'none';
											}
										}
									} else {
										console.error(
											`SP Resource List (${ blockId }): Invalid data format received from Filter API. Expected { html: string, totalPages: number (>=0) }, got:`,
											data
										);
										throw new Error(
											'Invalid response format from Filter API.'
										);
									}
									blockElement.style.opacity = '1';
								} )
								.catch( ( error ) => {
									console.error(
										`SP Resource List (${ blockId }): Error fetching filtered resources:`,
										error
									);
									blockElement.innerHTML =
										'<p>Error loading resources. Please try again.</p>';
									blockElement.style.opacity = '1';
								} );
						} catch ( error ) {
							console.error(
								`SP Resource List (${ blockId }): Unexpected error in change handler:`,
								error
							);
							if ( blockElement ) {
								blockElement.style.opacity = '1';
							}
						}
					} );
				} );
			} // end else (has radio buttons)
		} // end else (has filter form)

		// --- Load More Button Logic ---
		const loadMoreWrapper = blockElement.nextElementSibling;
		let loadMoreButton;
		if (
			loadMoreWrapper &&
			loadMoreWrapper.classList.contains(
				'sp-resource-list-load-more-wrapper'
			)
		) {
			loadMoreButton = loadMoreWrapper.querySelector(
				'.sp-resource-list-load-more'
			);
		}

		if ( loadMoreButton ) {
			loadMoreButton.addEventListener( 'click', ( event ) => {
				try {
					loadMoreButton.disabled = true;
					loadMoreButton.textContent = 'Loading...';

					const currentPageRaw =
						blockElement.dataset.currentPage || '1';
					let currentPage = parseInt( currentPageRaw, 10 );
					if ( isNaN( currentPage ) || currentPage < 1 ) {
						currentPage = 1;
					}

					const totalPagesRaw =
						blockElement.dataset.totalPages || '1';
					let totalPages = parseInt( totalPagesRaw, 10 );
					if ( isNaN( totalPages ) || totalPages < 0 ) {
						totalPages = 1;
					}

					// Read necessary parameters from stored state
					// Note: postsPerPage, selectionMode, orderBy, order, ids are read from initial state
					// selectedResourceTypes and currentFilterType are read from the block's current state

					const nextPage = currentPage + 1;

					if ( nextPage > totalPages ) {
						console.warn(
							`SP Resource List (${ blockId }): Attempted to load page ${ nextPage } beyond total ${ totalPages }.`
						); // Keep warning
						loadMoreButton.textContent = 'No More Resources';
						loadMoreWrapper.style.display = 'none';
						return;
					}

					// Construct API URL for Load More
					const loadMoreParams = new URLSearchParams();
					loadMoreParams.append( 'page', nextPage );
					loadMoreParams.append( 'postsPerPage', postsPerPage );
					loadMoreParams.append( 'selectionMode', selectionMode );
					loadMoreParams.append( 'orderBy', orderBy );
					loadMoreParams.append( 'order', order );
					loadMoreParams.append( 'resourceType', currentFilterType ); // Pass the *active* filter
					if ( selectionMode === 'taxonomy' ) {
						loadMoreParams.append(
							'selectedResourceTypes',
							JSON.stringify( selectedResourceTypes )
						);
					}
					if ( selectionMode === 'specific' && ids ) {
						loadMoreParams.append( 'ids', ids );
					}
					const loadMoreApiUrl = `/wp-json/sprypoint/v1/resource-list-load-more?${ loadMoreParams.toString() }`;

					fetch( loadMoreApiUrl )
						.then( ( response ) => {
							if ( ! response.ok ) {
								return response
									.json()
									.then( ( errData ) => {
										throw new Error(
											`HTTP error ${
												response.status
											}. Message: ${
												errData?.message ||
												'Unknown server error'
											}`
										);
									} )
									.catch( () => {
										throw new Error(
											`HTTP error! Status: ${ response.status }`
										);
									} );
							}
							return response.json();
						} )
						.then( ( data ) => {
							if (
								data &&
								typeof data.html === 'string' &&
								typeof data.currentPage === 'number' &&
								data.currentPage === nextPage &&
								typeof data.hasMorePages === 'boolean'
							) {
								blockElement.insertAdjacentHTML(
									'beforeend',
									data.html
								);
								blockElement.dataset.currentPage =
									data.currentPage;

								if ( ! data.hasMorePages ) {
									loadMoreWrapper.style.display = 'none';
								} else {
									loadMoreButton.disabled = false;
									loadMoreButton.textContent = 'Load more';
								}
							} else {
								console.error(
									`SP Resource List (${ blockId }): Invalid data format received from Load More API. Expected { html: string, currentPage: number (matching requested), hasMorePages: boolean }, got:`,
									data
								);
								throw new Error(
									'Invalid response format from Load More API.'
								);
							}
						} )
						.catch( ( error ) => {
							console.error(
								`SP Resource List (${ blockId }): Error fetching more resources:`,
								error
							);
							loadMoreButton.textContent = 'Error Loading';
							loadMoreButton.disabled = false;
						} );
				} catch ( error ) {
					console.error(
						`SP Resource List (${ blockId }): Unexpected error in load more click handler:`,
						error
					);
					if ( loadMoreButton ) {
						loadMoreButton.textContent = 'Error';
						loadMoreButton.disabled = false;
					}
				}
			} );
		} else {
			// No load more button found initially
		}
		// --- End Load More Button Logic ---
	} );
} );
